import { Form, FormInputValues } from "@/form";
import http from "@/http";
import utils from "@/utils";
import { Controller } from "@hotwired/stimulus";
import { Form as FrameForm, FrameApi, FrameContext, FrameHistoryStrategy } from "./frame-controller";

/** 
 * This controller should be added to the same form element as the frame controller.
 * It provides dependencies for the frame controller, in a way that decouples the controller from other modules.
 * The form implementaiton integrates with the form module to provide a way to get and set form variables.
 * The api implementation integrates with the http module to send the content request, handle errors, and display a spinner over the frame while the request is loading.
 * The history strategy implementation stores all of the variables in the URL so that the server can preload the last-viewed frame content when refreshing or navigating back to the page. It uses `replaceState`, so that the user only has to hit the back button once to return to the previous page. This means that any intermediate states are not saved in the browser history. Warning: variable names must be unique across all frames on the page. Otherwise they will overwrite each other in the URL.
 * All together, these dependencies cause the frame controller to act similar to a view switch, with the difference that the content does not get updated until the form is submitted.
 **/
export class FrameContextController extends Controller<HTMLFormElement> implements FrameForm, FrameApi, FrameHistoryStrategy {

    private form: Form;

    initialize(): void {
        utils.addControllerAction(this, {event: `${this.identifier}:provideContext`, identifier: 'frame', method: 'injectContext'});
    }

    connect(): void {
        Form.initialize(this.element).then(form => {
            this.form = form;
            const context: FrameContext = { form: this, api: this, history: this };
            this.dispatch('provideContext', {detail: context});
        });
    }

    disconnect(): void {
        this.form = null;
    }

    getVariables(): unknown {
        return this.form.getInputValues();
    }

    setVariables(variables: unknown): void {
        this.form.setInputValues(variables as FormInputValues);
    }

    getContent(element: HTMLElement, variables: unknown): Promise<string> {
        let data = new FormData;
        data.append('nonce', http.getNonce());
        data.append('variables', JSON.stringify(variables));
        let request = http.postTo(http.withPageParams(this.element.action), data);
        let withToast = http.withToastOnError(request, 'Failed to load content');
        let withSpinner = http.withSpinner(withToast, element);
        return withSpinner.then(http.asText);
    }

    captureState(variables: unknown): void {
        const url = http.resolveUrl(null, variables as Record<string, string>);
        history.replaceState(null, '', url);
    }
}
